import React, { useMemo, useCallback, useState, useEffect, useRef, forwardRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import moment from 'moment/moment'
import PropTypes from 'prop-types'

import { isNil } from 'lodash'
import filter from 'lodash/filter'
import find from 'lodash/find'
import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import toLower from 'lodash/toLower'

import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'

import harvestSchema from '@galo-monitor/forms/schemas/farm/harvest.schema'
import I18n, { useT } from '@galo-monitor/i18n'
import useGaloMonitorApi from '@galo-monitor/services/hooks/useGaloMonitorApi'
import { useSnackbar } from '@galo-monitor/snackbar'
import { selectLanguage } from '@galo-monitor/stores/language/selectorLanguage'
import { PropertyActions } from '@galo-monitor/stores/property'
import { selectUnitOfMeasurement } from '@galo-monitor/stores/property/selectorProperty'
import { momentBackDateFormat } from '@galo-monitor/utils/dates'
import Checkbox from '@galo-monitor/web-components/Checkbox'
import Form from '@galo-monitor/web-components/Form'
import InputColor from '@galo-monitor/web-components/InputColor'
import InputDate from '@galo-monitor/web-components/InputDate'
import InputSelect from '@galo-monitor/web-components/InputSelect'
import InputText from '@galo-monitor/web-components/InputText'

import { PlotsList } from './theme'

const HarvestForm = forwardRef((props, formRef) => {
  const { harvest, loading, setLoading, onSuccess, onError } = props
  const dispatch = useCallback(useDispatch(), [])
  const t = useT()
  const snackbar = useSnackbar()

  const { selected } = useSelector(selectLanguage)
  const { initials } = useSelector(selectUnitOfMeasurement)

  const { data: harvestData } = useGaloMonitorApi(harvest.id ? `/farms/harvests/properties/${ harvest.id }` : null)
  const { data: cultures } = useGaloMonitorApi('/farms/cultures')
  const { data: plots } = useGaloMonitorApi('/farms/plots/properties')

  const [harvestPlots, setHarvestPlots] = useState([])

  const cultureOptions = useMemo(
    () => map(cultures, culture => ({
      label: culture.name,
      value: culture.id.toString()
    })),
    [cultures]
  )

  const cultivationMethodsOptions = useMemo(
    () => [
      {
        label: t('cultivation method label conventional'),
        value: 'conventional'
      },
      {
        label: t('cultivation method label organic'),
        value: 'organic'
      },
      {
        label: t('cultivation method label hydroponic'),
        value: 'hydroponic'
      }
    ],
    [t]
  )

  const mounted = useRef(true)

  const handleSubmit = useCallback(
    (data) => {
      setLoading(true)
      dispatch(PropertyActions.saveHarvest(
        {
          ...harvest,
          ...data,
          plots: harvestPlots
        },
        () => {
          snackbar.success(t('{this} {action} successfully!', {
            this: t('plot', { howMany: 1 }),
            action: toLower(t(isEmpty(harvest) ? 'created' : 'updated', { gender: 'female', howMany: 1 }))
          }))
          onSuccess()
        },
        onError
      ))
    },
    [dispatch, harvest, harvestPlots, onError, onSuccess, setLoading, snackbar, t]
  )

  useEffect(() => () => {
    mounted.current = false
  }, [])

  useEffect(() => {
    if (!isNil(harvestData))
      setHarvestPlots(harvestData.plots)
  }, [harvestData])

  return (
    <Form
      ref={ formRef }
      schemaConstructor={ harvestSchema }
      onSubmit={ handleSubmit }
    >
      <Grid container spacing={ 2 }>
        <Grid item xs={ 12 } sm={ 6 }>
          <InputText
            name="name"
            label={ t('name of {this}', { this: t('harvest', { howMany: 1 }), howMany: 1, gender: 'female' }) }
            defaultValue={ harvest.name }
            disabled={ loading }
            fullWidth
          />
        </Grid>
        <Grid item xs={ 12 } sm={ 3 }>
          <InputDate
            label={ t('start date') }
            name="date_initial"
            defaultValue={ moment(harvest.dateInitial).format(momentBackDateFormat) }
            locale={ selected }
            fullWidth
          />
        </Grid>
        <Grid item xs={ 12 } sm={ 3 }>
          <InputDate
            label={ t('end date') }
            name="date_finish"
            defaultValue={ moment(harvest.dateFinish).format(momentBackDateFormat) }
            locale={ selected }
            fullWidth
          />
        </Grid>
        <Grid item xs={ 12 } sm={ 4 }>
          <InputSelect
            name="culture_id"
            label={ t('select the {this}', { this: t('culture', { howMany: 1 }), howMany: 1, gender: 'female' }) }
            defaultValue={ (harvest?.culture?.id || '').toString() }
            options={ cultureOptions }
            disabled={ loading }
            fullWidth
          />
        </Grid>
        <Grid item xs={ 12 } sm={ 4 }>
          <InputSelect
            name="cultivation_method"
            label={ t('select the {this}', { this: t('cultivation method'), howMany: 1, gender: 'male' }) }
            defaultValue={ (harvest.cultivationMethod || '').toString() }
            options={ cultivationMethodsOptions }
            disabled={ loading }
            fullWidth
          />
        </Grid>
        <Grid item xs={ 12 } sm={ 4 }>
          <InputColor
            name="color"
            label={ t('color') }
            defaultValue={ harvest?.color || '' }
            fullWidth
          />
        </Grid>
        <Grid item xs={ 12 }>
          <Typography variant="h6">
            {t('plot', { howMany: 2 })}
            {' *'}
          </Typography>
          <PlotsList container>
            {isEmpty(plots)
              ? <Grid item xs={ 12 }><I18n>empty select options</I18n></Grid>
              : map(plots, ({ plot, convertedArea }) => (
                <Grid key={ plot.id } item xs={ 6 }>
                  <Checkbox
                    detached
                    id={ `${ plot.id }` }
                    label={ `${ plot.name } / ${ convertedArea } ${ initials }` }
                    color="primary"
                    checked={ !!find(harvestPlots, harvestPlot => harvestPlot.id === plot.id) }
                    onChange={ () => {
                      if (find(harvestPlots, harvestPlot => harvestPlot.id === plot.id)) {
                        setHarvestPlots(prevState => filter(prevState, prevStateMod => prevStateMod.id !== plot.id))
                      } else {
                        setHarvestPlots(prevState => [...prevState, { id: plot.id }])
                      }
                    } }
                  />
                </Grid>
              ))
            }
          </PlotsList>
        </Grid>
      </Grid>
    </Form>
  )
})

HarvestForm.propTypes = {
  harvest: PropTypes.object,
  loading: PropTypes.bool,
  setLoading: PropTypes.func,
  onSuccess: PropTypes.func,
  onError: PropTypes.func
}

HarvestForm.defaultProps = {
  harvest: {},
  loading: false,
  setLoading: () => {},
  onSuccess: () => {},
  onError: () => {}
}

export default HarvestForm
